import useLocalStorageState from "use-local-storage-state";
import useCurrentUser from "./useCurrentUser";
import { useTransition } from "react";

const COLLAPSIBLES_KEY = "collapsibles";

export enum EReportsCollapsibleKeys {
  LATEST_TRANSACTIONS_TABLE = "latest-transactions-table",
  LATEST_TRANSACTIONS_CHART = "latest-transactions-chart",
  MONTHLY_TRANSACTIONS_TABLE = "monthly-transactions-table",
  MONTHLY_TRANSACTIONS_CHART = "monthly-transactions-chart",
  CATEGORY_TRANSACTIONS_TABLE = "category-transactions-table",
  CATEGORY_TRANSACTIONS_CHART = "category-transactions-chart",
  CLIENT_PROVIDER_TRANSACTIONS_TABLE = "client-provider-transactions-table",
  CLIENT_PROVIDER_TRANSACTIONS_CHART = "client-provider-transactions-chart",
  PAYMENT_METHOD_TRANSACTIONS_TABLE = "payment-methods-table",
  PAYMENT_METHOD_TRANSACTIONS_CHART = "payment-methods-chart",
  STATUS_TRANSACTIONS_TABLE = "status-transactions-table",
  STATUS_TRANSACTIONS_CHART = "status-transactions-chart",
}

export enum ENotificationsCollapsibleKeys {
  OVERDUE_TRANSACTIONS_COLLAPSIBLE = "overdue-transactions-collapsible",
  UPCOMING_TRANSACTIONS_COLLAPSIBLE = "upcoming-transactions-collapsible",
  TODAY_TRANSACTIONS_COLLAPSIBLE = "today-transactions-collapsible",
}

export enum ERecurringTransactionsCollapsibleKeys {
  OVERDUE_TRANSACTIONS_COLLAPSIBLE = "overdue-transactions-collapsible",
  UPCOMING_TRANSACTIONS_COLLAPSIBLE = "upcoming-transactions-collapsible",
  TODAY_TRANSACTIONS_COLLAPSIBLE = "today-transactions-collapsible",
  ALL_TRANSACTIONS_TABLE = "all-transactions-table",
}

export enum EAttachmentsCollapsibleKeys {
  FILE_TYPES_COLLAPSIBLE = "file-types-collapsible",
  ATTACHMENTS_TABLE = "attachments-table",
}

export const COLLAPSIBLES_DEFAULTS = {
  reports: {
    [EReportsCollapsibleKeys.LATEST_TRANSACTIONS_TABLE]: true,
    [EReportsCollapsibleKeys.MONTHLY_TRANSACTIONS_TABLE]: true,
    [EReportsCollapsibleKeys.CATEGORY_TRANSACTIONS_TABLE]: true,
    [EReportsCollapsibleKeys.CLIENT_PROVIDER_TRANSACTIONS_TABLE]: true,
    [EReportsCollapsibleKeys.PAYMENT_METHOD_TRANSACTIONS_TABLE]: true,
    [EReportsCollapsibleKeys.STATUS_TRANSACTIONS_TABLE]: true,
    [EReportsCollapsibleKeys.LATEST_TRANSACTIONS_CHART]: true,
    [EReportsCollapsibleKeys.MONTHLY_TRANSACTIONS_CHART]: true,
    [EReportsCollapsibleKeys.CATEGORY_TRANSACTIONS_CHART]: true,
    [EReportsCollapsibleKeys.CLIENT_PROVIDER_TRANSACTIONS_CHART]: true,
    [EReportsCollapsibleKeys.PAYMENT_METHOD_TRANSACTIONS_CHART]: true,
    [EReportsCollapsibleKeys.STATUS_TRANSACTIONS_CHART]: true,
  },
  notifications: {
    [ENotificationsCollapsibleKeys.TODAY_TRANSACTIONS_COLLAPSIBLE]: true,
    [ENotificationsCollapsibleKeys.OVERDUE_TRANSACTIONS_COLLAPSIBLE]: true,
    [ENotificationsCollapsibleKeys.UPCOMING_TRANSACTIONS_COLLAPSIBLE]: true,
  },
  recurring: {
    [ERecurringTransactionsCollapsibleKeys.TODAY_TRANSACTIONS_COLLAPSIBLE]: true,
    [ERecurringTransactionsCollapsibleKeys.OVERDUE_TRANSACTIONS_COLLAPSIBLE]: true,
    [ERecurringTransactionsCollapsibleKeys.UPCOMING_TRANSACTIONS_COLLAPSIBLE]: true,
    [ERecurringTransactionsCollapsibleKeys.ALL_TRANSACTIONS_TABLE]: true,
  },
  attachments: {
    [EAttachmentsCollapsibleKeys.ATTACHMENTS_TABLE]: true,
    [EAttachmentsCollapsibleKeys.FILE_TYPES_COLLAPSIBLE]: true,
  },
  shared: {
    accountOpen: true,
  },

  accountOpen: true,
  transactionsTableOpen: true,
  transactionsChartOpen: true,
  quickCalculationsOpen: true,
  summaryOpen: true,
  generalAdjustmentsOpen: false,
  selectionOpen: true,
  onlySumSelectionOpen: true,
  statsTableOpen: true,
  statsChartOpen: true,
  loansTableOpen: true,
  loansSummaryOpen: true,

  transactionHeaderOpen: true,
  transactionNotesOpen: true,
  transactionAttachmentsOpen: true,
  transactionDetailsOpen: true,
  transactionAmountsOpen: true,
  transactionPaymentOpen: true,
  transactionStatusOpen: true,
  fileInfoOpen: true,
  transactionInfoOpen: true,
  searchDatesOpen: false,
  searchCommonFieldsOpen: true,
  navIsCollapsed: true,
  pendingToPayOpen: true,
  pendingToCollectOpen: true,
  receivedInvitesOpen: true,
  moneyTransferDataOpen: true,
  moneyTransferAttachmentsOpen: true,
  summaryGeneralOpen: true,
  summaryTransactionsOpen: true,
  summaryPendingOpen: true,
  summaryContactRequestsOpen: true,
  summaryAttachmentsOpen: true,
  lightboxThumbnailsOpen: true,
  attachmentsTableOpen: true,
  attachmentsSummaryOpen: true,

  //
  appearanceThemeOpen: true,
  appearanceTransactionsOpen: true,
  appearanceBalanceOpen: true,
  appearanceMonthsOpen: true,
  appearanceDaysOpen: true,
  appearanceAnimationOpen: true,
  appearanceStatusesOpen: true,
  appearanceFilesOpen: true,
  appearanceOthersOpen: true,

  //
  tablePreferencesPaginationOpen: true,
  tablePreferencesColumnsOpen: true,
  tablePreferencesSelectionOpen: true,
  tablePreferencesClickBehaviorOpen: true,
  tablePreferencesBordersOpen: true,
  tablePreferencesIconsOpen: true,

  //
  chartPreferencesGeneralOpen: true,
  chartPreferencesIncomeOpen: true,
  chartPreferencesExpensesOpen: true,

  //
  recurringTransactionAttachmentsOpen: true,
  recurringTransactionDetailsOpen: true,

  //
  recurringTransactionsOverdueOpen: true,
  recurringTransactionsDueSoonOpen: true,
  recurringTransactionsTodayOpen: true,
  recurringTransactionsAllOpen: true,
  recurringTransactionsSummaryOpen: true,

  //
  sourcesIconAndColorsOpen: true,
  sourcesContactDataOpen: true,

  //
  exportTransactionsWhatToExportOpen: true,
  exportTransactionsAdditionalOptionsOpen: true,
  exportTransactionsFileInfoOpen: true,

  //
  paymentMethodsBuiltInOpen: true,
  paymentMethodsCustomOpen: true,

  // contacts
  contactsPageAcceptedOpen: true,
  contactsPageReceivedOpen: true,
  contactsPageSentOpen: true,

  //
  recurrenceHistoryTableOpen: true,

  // dashboard
  dashboardPendingContactInvitesOpen: true,
  dashboardAccountsBalanceChartOpen: true,
  dashboardLatestTransactionsOpen: true,
  dashboardAccountsOpen: true,
  dashboardAvailablePerCurrencyOpen: true,
  dashboardExtensionsOpen: true,
  pendingTransactionsBlockOpen: true,
};

export default function useCollapsibles() {
  const [isPending, startTransition] = useTransition();
  const { userInfo } = useCurrentUser();
  const [collapsiblePreferences, setCollapsiblePreferences] =
    useLocalStorageState(`${userInfo?._id}-${COLLAPSIBLES_KEY}`, {
      defaultValue: COLLAPSIBLES_DEFAULTS,
    });

  const resetCollapsiblesToDefaults = () => {
    setCollapsiblePreferences(COLLAPSIBLES_DEFAULTS);
  };

  return {
    collapsiblePreferences,
    updateCollapsible: (newState: Partial<typeof COLLAPSIBLES_DEFAULTS>) => {
      startTransition(() =>
        setCollapsiblePreferences((prev: any) => ({ ...prev, ...newState })),
      );
    },
    resetCollapsiblesToDefaults,
  };
}
